import { useState, useEffect } from "react";
import "../index.css";
import Footer from "../components/Footer";
import { PageNav } from "../components/PageNav";
import { useNavigate } from "react-router-dom";
import ApiCall from "../auth/ApiCall";
import { cacheProfileSetting } from "../auth/UserProfile";
import type { SubscriptionPlan } from "../types/planTypes";
import { plans } from "../types/planTypes";

// interface SubscriptionPlan {
//   idMonthly: string;
//   idAnnual: string;
//   nameMonthly: string;
//   nameAnnual: string;
//   priceMonthly: number;
//   priceAnnual: number;
//   features: string[];
//   stripePriceIdMonthly: string;
//   stripePriceIdAnnual: string;
//   popular?: boolean;
// }

export default function SubscriptionUpdate() {
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showMonthly, setShowMonthly] = useState<boolean>(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState<boolean>(false);
  const [subscriptionExpiration, setSubscriptionExpiration] = useState<
    string | null
  >(null);
  const [isTrialState, setIsTrialState] = useState<boolean>(false);
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [canManageInApp, setCanManageInApp] = useState<boolean>(true);
  const [isCancelled, setIsCancelled] = useState<boolean>(() => {
    const stateId = localStorage.getItem("state_id");
    return stateId === "5" || stateId === "6";
  });
  const [platformName, setPlatformName] = useState<string>("");
  //  const [managementUrl, setManagementUrl] = useState<string>(""); // eslint-disable-line
  const [managementLoading, setManagementLoading] = useState<boolean>(true);

  // Define subscription plans (same as Subscribe.tsx)
  // const plans: SubscriptionPlan[] = [
  //   {
  //     idMonthly: "basic_monthly",
  //     idAnnual: "basic_annual",
  //     nameMonthly: "Basic",
  //     nameAnnual: "Basic",
  //     priceMonthly: 9.99,
  //     priceAnnual: 99.99,
  //     stripePriceIdMonthly: "price_1SSHn9Dp8jvphDnHZ2A8hcUB",
  //     stripePriceIdAnnual: "price_1SSHneDp8jvphDnHUAV5oZVQ",
  //     features: [
  //       "Access to basic features",
  //       "Email support",
  //       "Limited alerts",
  //       "Mobile app access",
  //     ],
  //   },
  //   {
  //     idMonthly: "pro_monthly",
  //     idAnnual: "pro_annual",
  //     nameMonthly: "Pro",
  //     nameAnnual: "Pro",
  //     priceMonthly: 19.99,
  //     priceAnnual: 199.99,
  //     stripePriceIdMonthly: "price_1SSHoADp8jvphDnHqPcWUdeh",
  //     stripePriceIdAnnual: "price_1SSHodDp8jvphDnHnWGzjayU",
  //     features: [
  //       "All Basic features",
  //       "Unlimited alerts",
  //       "Priority support",
  //       "Advanced analytics",
  //       "Custom notifications",
  //     ],
  //     popular: true,
  //   },
  //   {
  //     idMonthly: "premium_monthly",
  //     idAnnual: "premiun_annual",
  //     nameMonthly: "Premium",
  //     nameAnnual: "Premium",
  //     priceMonthly: 24.99,
  //     priceAnnual: 249.99,
  //     stripePriceIdMonthly: "price_1SSHp0Dp8jvphDnHRexFM2Oz",
  //     stripePriceIdAnnual: "price_1SSHpMDp8jvphDnHk52K3Kl7",
  //     features: [
  //       "All Pro features",
  //       "Save 2 months",
  //       "Dedicated support",
  //       "Early access to new features",
  //       "Custom integrations",
  //     ],
  //   },
  // ];

  // Redirect new users (state 1) to subscribe page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const stateId = localStorage.getItem("state_id");
    if (stateId === "1") {
      navigate("/subscribe");
    }
  }, [navigate]);

  // Load current subscription on mount
  useEffect(() => {
    const subId = localStorage.getItem("subscription_id");
    const pendingSubId = localStorage.getItem("pending_subscription_id");
    // const subName = localStorage.getItem("subscription_name");
    const pendingSubName = localStorage.getItem("pending_subscription_name");
    const subExp = localStorage.getItem("subscription_expiration");
    const stateId = localStorage.getItem("state_id");

    setCurrentPlan(subId);
    setPendingPlan(pendingSubName);
    setPendingPlanId(pendingSubId);
    setSubscriptionExpiration(subExp);

    // Check if user is in trial state (state_id == 2)
    setIsTrialState(stateId === "2");

    // Determine if current plan is monthly or annual
    if (subId?.includes("monthly")) {
      setShowMonthly(true);
    }

    // Fetch platform management info
    fetchManagementInfo();
  }, []);

  const fetchManagementInfo = async () => {
    try {
      setManagementLoading(true);
      const response = (await ApiCall.makeAuthenticatedRequest(
        "/accounts/api/payments/subscription/management-info/",
      )) as {
        can_manage_in_app?: boolean;
        platform_name?: string;
        management_url?: string;
      };

      // Default to true if not explicitly set
      const canManage = response.can_manage_in_app !== false;

      setCanManageInApp(canManage);
      setPlatformName(response.platform_name || "");
      //      setManagementUrl(response.management_url || "");
    } catch {
      // console.error("Failed to fetch management info:", err);
      // Default to allowing in-app management if the call fails
      setCanManageInApp(true);
    } finally {
      setManagementLoading(false);
    }
  };

  // Helper function to format subscription date
  const formatSubscriptionDate = (dateString: string | null): string => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Get current plan details
  const getCurrentPlanDetails = () => {
    if (!currentPlan || currentPlan.length === 0) return null;

    for (const plan of plans) {
      // const lowerCurrentPlan = currentPlan.toLocaleLowerCase();
      if (currentPlan === plan.idAnnual || currentPlan === plan.idMonthly) {
        const isMonthly = currentPlan === plan.idMonthly;
        return {
          name: isMonthly ? plan.nameMonthly : plan.nameAnnual,
          price: isMonthly ? plan.priceMonthly : plan.priceAnnual,
          interval: isMonthly ? "month" : "year",
        };
      }
    }
    return null;
  };

  const currentPlanDetails = getCurrentPlanDetails();

  const handleUpdateSubscription = async (plan: SubscriptionPlan) => {
    setError("");
    setIsLoading(true);
    const newPlanId = showMonthly ? plan.idMonthly : plan.idAnnual;
    setSelectedPlan(newPlanId);

    // Check if user is selecting the same plan
    if (newPlanId === currentPlan) {
      setError("You are already subscribed to this plan");
      setIsLoading(false);
      setSelectedPlan(null);
      return;
    }

    // Check if user is clicking on the pending plan to cancel the downgrade
    if (newPlanId === pendingPlanId) {
      const confirmCancel = window.confirm(
        "Are you sure you want to cancel the scheduled plan change and keep your current plan?",
      );

      if (!confirmCancel) {
        setIsLoading(false);
        setSelectedPlan(null);
        return;
      }

      try {
        // Cancel the pending downgrade by reverting to current plan
        const cancelPayload = {
          cancel_pending: true, // Flag to indicate we're canceling the pending change
        };

        const response = (await ApiCall.makeAuthenticatedRequest(
          "/accounts/api/payments/stripe/subscription/update/",
          {
            method: "POST",
            body: JSON.stringify(cancelPayload),
          },
        )) as {
          success?: boolean;
          message?: string;
          error?: string;
          platform_conflict?: boolean;
          existing_platform?: string;
          platform?: string;
          management_url?: string;
        };

        // Check for platform conflict
        if (response.platform_conflict || response.existing_platform) {
          const platform =
            response.existing_platform || response.platform || "mobile";
          const managementUrl = response.management_url || "";

          let errorMessage =
            `This subscription is managed through ${platform}. ` +
            `Please manage your subscription through your ${platform} account.`;

          if (managementUrl) {
            errorMessage += ` Visit: ${managementUrl}`;
          }

          throw new Error(errorMessage);
        }

        if (response.success || (response.message && !response.error)) {
          // Refresh profile to get latest data and update localStorage
          await cacheProfileSetting(navigate, "/settings");

          alert(
            response.message || "Pending plan change cancelled successfully!",
          );
          return;
        } else {
          throw new Error(response.error || "Failed to cancel pending change");
        }
      } catch (err) {
        // console.error("Error canceling pending downgrade:", err);

        // Enhanced error messaging
        if (err instanceof Error) {
          const errorMessage = err.message.toLowerCase();

          // Platform conflict errors
          if (
            errorMessage.includes("platform") ||
            errorMessage.includes("revenuecat") ||
            errorMessage.includes("apple") ||
            errorMessage.includes("google") ||
            errorMessage.includes("mobile")
          ) {
            setError(
              "This subscription is managed through a mobile platform. " +
                "Please manage your subscription through your mobile device.",
            );
          } else {
            setError(err.message);
          }
        } else {
          setError("Failed to cancel pending change. Please try again.");
        }
        setIsLoading(false);
        setSelectedPlan(null);
        return;
      }
    }

    // Confirm the subscription change with the user
    const planName = showMonthly ? plan.nameMonthly : plan.nameAnnual;
    const planPrice = showMonthly ? plan.priceMonthly : plan.priceAnnual;
    const planInterval = showMonthly ? "month" : "year";

    const confirmChange = window.confirm(
      `Are you sure you want to switch to ${planName} for $${planPrice}/${planInterval}?`,
    );

    if (!confirmChange) {
      setIsLoading(false);
      setSelectedPlan(null);
      return;
    }

    try {
      // Update subscription via backend API
      const requestPayload = {
        price_id: showMonthly
          ? plan.stripePriceIdMonthly
          : plan.stripePriceIdAnnual,
      };

      const response = (await ApiCall.makeAuthenticatedRequest(
        "/accounts/api/payments/stripe/subscription/update/",
        {
          method: "POST",
          body: JSON.stringify(requestPayload),
        },
      )) as {
        success?: boolean;
        message?: string;
        url?: string;
        redirect_to_checkout?: boolean;
        price_id?: string;
        error?: string;
        platform?: string;
        management_url?: string;
        subscription_id?: string;
        current_period_end?: string;
        effective_date?: string;
        note?: string;
        platform_conflict?: boolean;
        existing_platform?: string;
      };

      // Check for platform conflict
      if (response.platform_conflict || response.existing_platform) {
        const platform =
          response.existing_platform || response.platform || "mobile";
        const managementUrl = response.management_url || "";

        let errorMessage =
          `This subscription is managed through ${platform}. ` +
          `Please manage your subscription through your ${platform} account.`;

        if (managementUrl) {
          errorMessage += ` Visit: ${managementUrl}`;
        }

        throw new Error(errorMessage);
      }

      // Check for platform mismatch error (legacy format)
      if (response.error && response.platform && response.management_url) {
        throw new Error(
          `Your subscription was purchased through ${response.platform}. Please manage it at: ${response.management_url}`,
        );
      }

      // Check for downgrade scenario (scheduled for end of period)
      // Backend returns message + subscription_id for successful downgrades
      const isDowngrade =
        response.message && response.subscription_id && !response.error;

      if (isDowngrade) {
        // Treat as success - downgrade scheduled
        await cacheProfileSetting(navigate, "/settings");
        alert(response.message || "Subscription updated successfully!");
        return;
      }

      if (response.redirect_to_checkout) {
        // Backend needs user to enter payment method - redirect to checkout
        const checkoutResponse = (await ApiCall.makeAuthenticatedRequest(
          "/accounts/api/payments/stripe/create-checkout/",
          {
            method: "POST",
            body: JSON.stringify({
              price_id: response.price_id,
              success_url: window.location.origin + "/settings",
              cancel_url: window.location.origin + "/subscription/update",
            }),
          },
        )) as { url?: string; sessionId?: string; session_url?: string };

        if (checkoutResponse.url) {
          window.location.href = checkoutResponse.url;
        } else if (checkoutResponse.session_url) {
          window.location.href = checkoutResponse.session_url;
        } else {
          throw new Error("No checkout URL received");
        }
        return;
      }

      if (response.success) {
        // Refresh user profile data from backend to update all localStorage values
        await cacheProfileSetting(navigate, "/settings");

        // Show success message
        alert("Subscription updated successfully!");
      } else if (response.url) {
        // If backend returns a checkout URL (for payment method update)
        window.location.href = response.url;
      } else {
        // Show detailed error message from backend
        const errorMessage =
          response.error || response.message || "Failed to update subscription";
        throw new Error(errorMessage);
      }
    } catch (err) {
      // console.error("=== SUBSCRIPTION UPDATE ERROR ===");
      // console.error("Error Type:", typeof err);
      // console.error("Error Object:", err);
      // console.error(
      //   "Error Message:",
      //   err instanceof Error ? err.message : String(err),
      // );
      // console.error("Error Stack:", err instanceof Error ? err.stack : "N/A");
      // console.error("=================================");

      // Enhanced error messaging for common scenarios
      if (err instanceof Error) {
        const errorMessage = err.message.toLowerCase();

        // Platform conflict errors - user trying to manage from wrong platform
        if (
          errorMessage.includes("platform") ||
          errorMessage.includes("revenuecat") ||
          errorMessage.includes("apple") ||
          errorMessage.includes("google") ||
          errorMessage.includes("mobile")
        ) {
          setError(
            "This subscription is managed through a mobile platform. " +
              "Please manage your subscription through your mobile device.",
          );
        }
        // Receipt validation errors
        else if (
          errorMessage.includes("receipt") ||
          errorMessage.includes("validation")
        ) {
          setError(
            "There was a problem validating your subscription. " +
              "Please contact support if this issue persists.",
          );
        }
        // Network/connection errors
        else if (
          errorMessage.includes("network") ||
          errorMessage.includes("connection") ||
          errorMessage.includes("timeout")
        ) {
          setError(
            "Network error. Please check your connection and try again.",
          );
        }
        // Stripe-specific payment errors
        else if (
          errorMessage.includes("payment method") ||
          errorMessage.includes("card")
        ) {
          setError(
            "There was an issue with your payment method. " +
              "Please update your payment information and try again.",
          );
        }
        // Default to original error message
        else {
          setError(err.message);
        }
      } else {
        setError("Failed to update subscription. Please try again.");
      }
    } finally {
      setIsLoading(false);
      setSelectedPlan(null);
    }
  };

  const handleCancelSubscription = async () => {
    setError("");
    setIsLoading(true);

    try {
      const response = (await ApiCall.makeAuthenticatedRequest(
        "/accounts/api/payments/stripe/subscription/cancel/",
        {
          method: "POST",
        },
      )) as {
        success?: boolean;
        message?: string;
        error?: string;
        statusCode?: number;
        platform?: string;
        management_url?: string;
        subscription_expiration?: string;
        cancels_at?: string;
        subscription_id?: string;
      };

      // Check for platform mismatch error
      if (response.error && response.platform && response.management_url) {
        throw new Error(
          `Your subscription was purchased through ${response.platform}. Please manage it at: ${response.management_url}`,
        );
      }

      // Check for API-level errors (non-2xx responses)
      if (response.error) {
        throw new Error(response.error);
      }

      // Accept either explicit success flag or cancels_at as a success indicator
      if (response.success || response.cancels_at) {
        // Don't clear subscription data — user retains access until period end.
        if (response.subscription_expiration) {
          localStorage.setItem(
            "subscription_expiration",
            response.subscription_expiration as string,
          );
        }
        // State 2 (trial) cancelled → 5; State 3 (active subscription) cancelled → 6
        localStorage.setItem("state_id", isTrialState ? "5" : "6");
        setIsCancelled(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        throw new Error(response.message || "Failed to cancel subscription");
      }
    } catch (err) {
      // console.error("Subscription cancellation error:", err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Failed to cancel subscription. Please try again.");
      }
    } finally {
      setIsLoading(false);
      setShowCancelConfirm(false);
    }
  };

  const displayMonthly = () => {
    setShowMonthly(true);
  };

  const displayYearly = () => {
    setShowMonthly(false);
  };

  // Helper function to get plan tier level for comparison
  const getPlanTier = (planId: string | null): number => {
    if (!planId) return -1;

    const planLower = planId.toLowerCase();
    if (planLower.includes("basic")) return 1;
    if (planLower.includes("pro")) return 2;
    if (planLower.includes("premium")) return 3;

    return -1;
  };

  // Helper function to determine if pending plan is same level as current
  const isSameTierChange = (): boolean => {
    if (!currentPlan || !pendingPlanId) return false;
    return getPlanTier(currentPlan) === getPlanTier(pendingPlanId);
  };

  const getPlanBadge = (plan: SubscriptionPlan) => {
    const planId = showMonthly ? plan.idMonthly : plan.idAnnual;

    // Check if this is the current plan
    if (planId === currentPlan) {
      return (
        <div
          style={{
            position: "absolute",
            top: "-12px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#2196F3",
            color: "white",
            padding: "4px 16px",
            borderRadius: "20px",
            fontSize: "12px",
            fontWeight: "bold",
          }}
          className="font_family_base"
        >
          CURRENT PLAN
        </div>
      );
    }

    // Check if this is the pending plan (scheduled downgrade)
    if (planId === pendingPlanId) {
      return (
        <div
          style={{
            position: "absolute",
            top: "-12px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#ff9800",
            color: "white",
            padding: "4px 16px",
            borderRadius: "20px",
            fontSize: "12px",
            fontWeight: "bold",
          }}
          className="font_family_base"
        >
          PENDING
        </div>
      );
    }

    if (plan.popular) {
      return (
        <div
          style={{
            position: "absolute",
            top: "-12px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#4CAF50",
            color: "white",
            padding: "4px 16px",
            borderRadius: "20px",
            fontSize: "12px",
            fontWeight: "bold",
          }}
          className="font_family_base"
        >
          MOST POPULAR
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-overlay-spinner"></div>
        </div>
      )}

      <PageNav />

      <div className="main_div" style={{ paddingTop: 20, paddingBottom: 40 }}>
        <div className="center_me">
          <h1
            className="font_family_base"
            style={{ fontSize: 32, marginBottom: 10 }}
          >
            Update Your Subscription
          </h1>
          {/* <p
            className="font_family_base"
            style={{ color: "#666", marginBottom: 30, fontSize: 14 }}
          >
            Upgrade, downgrade, or cancel your subscription
          </p> */}
        </div>

        {/* Cancellation Confirmation Banner */}
        {isCancelled && (
          <div
            className="center_me"
            style={{
              maxWidth: 600,
              margin: "0 auto 30px auto",
              padding: "20px 24px",
              backgroundColor: "#1a2a1a",
              border: "1px solid #4CAF50",
              borderRadius: 12,
            }}
          >
            <p
              className="font_family_base"
              style={{ color: "#4CAF50", fontWeight: "bold", fontSize: 16, marginBottom: 6 }}
            >
              Subscription Cancelled
            </p>
            <p
              className="font_family_base"
              style={{ color: "#AAA", fontSize: 14, margin: 0 }}
            >
              Your subscription has been cancelled. You will retain access until
              the end of your current billing period
              {subscriptionExpiration
                ? ` (${formatSubscriptionDate(subscriptionExpiration)})`
                : ""}
              .
            </p>
          </div>
        )}

        {/* Current Subscription Info */}
        {currentPlanDetails && !isCancelled && (
          <div
            className="center_me"
            style={{
              // maxWidth: "600px",
              minWidth: "320px",
              margin: "0 10px 10px 10px",
              padding: "20px",
            }}
          >
            <div
              style={{
                backgroundColor: "#1a1a1a",
                minWidth: "320px",
                border: "2px solid #2196F3",
                borderRadius: "12px",
                padding: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 15,
                }}
              >
                <div>
                  <p
                    className="font_family_base"
                    style={{ color: "#AAA", fontSize: 12, marginBottom: 5 }}
                  >
                    CURRENT PLAN
                  </p>
                  <h3
                    className="font_family_base"
                    style={{ fontSize: 24, margin: 0 }}
                  >
                    {currentPlanDetails.name}
                  </h3>
                </div>
                <div style={{ textAlign: "right" }}>
                  <p
                    className="font_family_base"
                    style={{ fontSize: 28, fontWeight: "bold", margin: 0 }}
                  >
                    ${currentPlanDetails.price}
                  </p>
                  <p
                    className="font_family_base"
                    style={{ color: "#AAA", fontSize: 14, margin: 0 }}
                  >
                    per {currentPlanDetails.interval}
                  </p>
                </div>
              </div>
              <div
                style={{
                  borderTop: "1px solid #333",
                  paddingTop: 15,
                  marginTop: 15,
                }}
              >
                <p
                  className="font_family_base"
                  style={{ color: "#AAA", fontSize: 14, margin: 0 }}
                >
                  Renews on:{" "}
                  <span style={{ color: "#FFF" }}>
                    {formatSubscriptionDate(subscriptionExpiration)}
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Pending Subscription Notice */}
        {pendingPlan && pendingPlan !== "" && (
          <div
            className="center_me"
            style={{
              backgroundColor: "#fff3e0",
              color: "#e65100",
              padding: "15px 20px",
              borderRadius: "8px",
              marginBottom: "30px",
              maxWidth: "600px",
              margin: "0 auto 30px auto",
              border: "2px solid #ff9800",
            }}
          >
            {/* <p
              className="font_family_base"
              style={{
                margin: 0,
                fontSize: 14,
                fontWeight: "bold",
                marginBottom: 8,
              }}
            >
              ⏳ Pending Plan Change
            </p> */}
            <p className="font_family_base" style={{ margin: 0, fontSize: 14 }}>
              <strong>⏳ Pending Plan Change:</strong> Your plan will change to{" "}
              <strong>{pendingPlan}</strong> at the end of your current billing
              cycle on {formatSubscriptionDate(subscriptionExpiration)}.
            </p>
          </div>
        )}

        {/* Platform Management Notice - for mobile users */}
        {!managementLoading && !canManageInApp && platformName && (
          <div
            className="center_me"
            style={{
              backgroundColor: "#e3f2fd",
              color: "#1565c0",
              padding: "15px 20px",
              borderRadius: "8px",
              maxWidth: "600px",
              margin: "0 auto 30px auto",
              boxSizing: "border-box",
              width: "calc(100% - 40px)",
              border: "2px solid #1976d2",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <p
              className="font_family_base"
              style={{
                margin: 0,
                fontSize: 14,
                fontWeight: "bold",
                marginBottom: 8,
              }}
            >
              📱 Manage via {platformName}
            </p>
            <p
              className="font_family_base"
              style={{ margin: 0, fontSize: 14, marginBottom: 10 }}
            >
              Your subscription was purchased through {platformName}. To update
              or cancel your subscription, please visit your {platformName}{" "}
              account settings.
            </p>
            {/* {managementUrl && (
              <a
                href={managementUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font_family_base"
                style={{
                  display: "inline-block",
                  padding: "8px 16px",
                  backgroundColor: "#1976d2",
                  color: "#FFF",
                  borderRadius: "4px",
                  textDecoration: "none",
                  fontSize: 14,
                  fontWeight: "bold",
                }}
              >
                Manage Subscription →
              </a>
            )} */}
          </div>
        )}

        {/* Only show plan management for Stripe users */}
        {canManageInApp && (
          <>
            {/* Billing Period Toggle */}
            <div className="row center_me margin_bottom_30">
              <button
                className={`font_family_base ${
                  showMonthly ? "bill-type-button-disabled" : "bill-type-button"
                }`}
                onClick={displayYearly}
              >
                Paid Yearly
              </button>
              <button
                className={`font_family_base ${
                  showMonthly ? "bill-type-button" : "bill-type-button-disabled"
                }`}
                onClick={displayMonthly}
              >
                Paid Monthly
              </button>
            </div>
          </>
        )}

        {/* Trial State Notice */}
        {canManageInApp &&
          (isTrialState ? (
            <div
              className="center_me"
              style={{
                backgroundColor: "#e3f2fd",
                color: "#1976d2",
                padding: "12px 20px",
                borderRadius: "8px",
                marginBottom: "30px",
                maxWidth: "600px",
                margin: "0 auto 30px auto",
                border: "1px solid #90caf9",
              }}
            >
              <p
                className="font_family_base"
                style={{ margin: 0, fontSize: 14 }}
              >
                <strong>Trial Period Notice:</strong> Updating your subscription
                will not end your trial period. You will continue to enjoy trial
                benefits until your trial expires.
              </p>
            </div>
          ) : (
            <div
              className="center_me"
              style={{
                backgroundColor: "#fff3e0",
                color: "#e65100",
                padding: "12px 20px",
                borderRadius: "8px",
                marginBottom: "30px",
                maxWidth: "600px",
                margin: "0 auto 30px auto",
                border: "1px solid #ffb74d",
              }}
            >
              <p
                className="font_family_base"
                style={{ margin: 0, fontSize: 14 }}
              >
                <strong>Important:</strong> Switching plans will not grant a new
                trial period. Your new plan will take effect immediately and you
                will be billed according to the selected billing cycle.
              </p>
            </div>
          ))}

        {error && (
          <div
            className="center_me"
            style={{
              backgroundColor: "#fee",
              color: "#c33",
              padding: "12px 20px",
              borderRadius: "8px",
              marginBottom: "30px",
              maxWidth: "600px",
              margin: "0 auto 30px auto",
              border: "1px solid #fcc",
            }}
          >
            {error}
          </div>
        )}

        {/* Only show plan cards for Stripe users */}
        {canManageInApp && (
          <>
            {/* Desktop View */}
            <div className="show-desktop">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                  gap: "20px",
                  maxWidth: "1200px",
                  margin: "0 auto",
                  padding: "0 20px",
                }}
              >
                {plans.map((plan) => {
                  const planId = showMonthly ? plan.idMonthly : plan.idAnnual;
                  // const isCurrentPlanType =
                  //   currentPlan
                  //     ?.toLocaleLowerCase()
                  //     .includes(plan.nameAnnual.toLocaleLowerCase()) ||
                  //   currentPlan
                  //     ?.toLocaleLowerCase()
                  //     .includes(plan.nameMonthly.toLocaleLowerCase());
                  // const isCurrentPlanDuration = showMonthly
                  //   ? currentPlan?.toLocaleLowerCase().includes("monthly")
                  //   : currentPlan?.toLocaleLowerCase().includes("annual");
                  //              const isCurrentPlan = planId === currentPlan;
                  const isCurrentPlan = planId === currentPlan;
                  // isCurrentPlanDuration && isCurrentPlanType;

                  return (
                    <div
                      key={planId}
                      style={{
                        border: isCurrentPlan
                          ? "2px solid #2196F3"
                          : planId === pendingPlanId
                            ? "2px solid #ff9800"
                            : plan.popular
                              ? "2px solid #4CAF50"
                              : "2px solid #ccc",
                        borderRadius: "12px",
                        padding: "30px",
                        backgroundColor: "#000",
                        position: "relative",
                        boxShadow: isCurrentPlan
                          ? "0 4px 12px rgba(33, 150, 243, 0.3)"
                          : planId === pendingPlanId
                            ? "0 4px 12px rgba(255, 152, 0, 0.3)"
                            : plan.popular
                              ? "0 4px 12px rgba(76, 175, 80, 0.2)"
                              : "0 2px 8px rgba(0,0,0,0.1)",
                        transition: "transform 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-5px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      {getPlanBadge(plan)}

                      <h2
                        className="font_family_base center_me"
                        style={{ fontSize: 24, marginBottom: 10 }}
                      >
                        {showMonthly ? plan.nameMonthly : plan.nameAnnual}
                      </h2>

                      <div style={{ marginBottom: 20 }}>
                        <span
                          className="font_family_base"
                          style={{ fontSize: 36, fontWeight: "bold" }}
                        >
                          ${showMonthly ? plan.priceMonthly : plan.priceAnnual}
                        </span>
                        <span
                          className="font_family_base"
                          style={{ color: "#AAA", fontSize: 16 }}
                        >
                          /{showMonthly ? "month" : "year"}
                        </span>
                      </div>

                      <ul
                        style={{
                          listStyle: "none",
                          padding: 0,
                          marginBottom: 30,
                          minHeight: "180px",
                        }}
                      >
                        {plan.features.map((feature, index) => (
                          <li
                            key={index}
                            className="font_family_base"
                            style={{
                              padding: "8px 0",
                              display: "flex",
                              alignItems: "center",
                              fontSize: 14,
                            }}
                          >
                            <span style={{ color: "#4CAF50", marginRight: 8 }}>
                              ✓
                            </span>
                            {feature}
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={() => handleUpdateSubscription(plan)}
                        disabled={isLoading || isCurrentPlan}
                        className="font_family_base"
                        style={{
                          width: "100%",
                          padding: "12px 24px",
                          backgroundColor: isCurrentPlan
                            ? "#2196F3"
                            : planId === pendingPlanId
                              ? "#ff9800"
                              : plan.popular
                                ? "#4CAF50"
                                : "#333333",
                          color: "white",
                          border: isCurrentPlan
                            ? "2px solid #1976d2"
                            : planId === pendingPlanId
                              ? "2px solid #f57c00"
                              : "none",
                          borderRadius: "8px",
                          fontSize: 16,
                          fontWeight: "bold",
                          cursor:
                            isLoading || isCurrentPlan
                              ? "not-allowed"
                              : "pointer",
                          opacity:
                            isLoading && selectedPlan === planId ? 0.6 : 1,
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          if (!isLoading && !isCurrentPlan) {
                            e.currentTarget.style.opacity = "0.9";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isCurrentPlan) {
                            e.currentTarget.style.opacity = "1";
                          }
                        }}
                      >
                        {isCurrentPlan
                          ? "✓ Current Plan"
                          : planId === pendingPlanId
                            ? isSameTierChange()
                              ? "Cancel Plan Change"
                              : "Cancel Downgrade"
                            : isLoading && selectedPlan === planId
                              ? "Processing..."
                              : "Switch to This Plan"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile View */}
            <div className="show-mobile" style={{ padding: "0 20px" }}>
              {plans.map((plan) => {
                const planId = showMonthly ? plan.idMonthly : plan.idAnnual;
                // const isCurrentPlan = planId === currentPlan;
                // const isCurrentPlanType =
                //   currentPlan
                //     ?.toLocaleLowerCase()
                //     .includes(plan.nameAnnual.toLocaleLowerCase()) ||
                //   currentPlan
                //     ?.toLocaleLowerCase()
                //     .includes(plan.nameMonthly.toLocaleLowerCase());
                // const isCurrentPlanDuration = showMonthly
                //   ? currentPlan?.toLocaleLowerCase().includes("monthly")
                //   : currentPlan?.toLocaleLowerCase().includes("annual");
                //              const isCurrentPlan = planId === currentPlan;
                const isCurrentPlan = planId === currentPlan;
                // isCurrentPlanDuration && isCurrentPlanType;

                return (
                  <div
                    key={planId}
                    style={{
                      border: isCurrentPlan
                        ? "2px solid #2196F3"
                        : planId === pendingPlanId
                          ? "2px solid #ff9800"
                          : plan.popular
                            ? "2px solid #4CAF50"
                            : "2px solid #ccc",
                      borderRadius: "12px",
                      padding: "24px",
                      backgroundColor: "#000",
                      marginBottom: "20px",
                      position: "relative",
                      boxShadow: isCurrentPlan
                        ? "0 4px 12px rgba(33, 150, 243, 0.3)"
                        : planId === pendingPlanId
                          ? "0 4px 12px rgba(255, 152, 0, 0.3)"
                          : plan.popular
                            ? "0 4px 12px rgba(76, 175, 80, 0.2)"
                            : "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    {getPlanBadge(plan)}

                    <h2
                      className="font_family_base center_me"
                      style={{ fontSize: 22, marginBottom: 10 }}
                    >
                      {showMonthly ? plan.nameMonthly : plan.nameAnnual}
                    </h2>

                    <div style={{ marginBottom: 16 }}>
                      <span
                        className="font_family_base"
                        style={{ fontSize: 32, fontWeight: "bold" }}
                      >
                        ${showMonthly ? plan.priceMonthly : plan.priceAnnual}
                      </span>
                      <span
                        className="font_family_base"
                        style={{ color: "#AAA", fontSize: 14 }}
                      >
                        {showMonthly ? "/month" : "/year"}
                      </span>
                    </div>

                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        marginBottom: 20,
                      }}
                    >
                      {plan.features.map((feature, index) => (
                        <li
                          key={index}
                          className="font_family_base"
                          style={{
                            padding: "6px 0",
                            display: "flex",
                            alignItems: "center",
                            fontSize: 13,
                          }}
                        >
                          <span style={{ color: "#4CAF50", marginRight: 8 }}>
                            ✓
                          </span>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => handleUpdateSubscription(plan)}
                      disabled={isLoading || isCurrentPlan}
                      className="font_family_base"
                      style={{
                        width: "100%",
                        padding: "12px 24px",
                        backgroundColor: isCurrentPlan
                          ? "#2196F3"
                          : planId === pendingPlanId
                            ? "#ff9800"
                            : plan.popular
                              ? "#4CAF50"
                              : "#333333",
                        color: "white",
                        border: isCurrentPlan
                          ? "2px solid #1976d2"
                          : planId === pendingPlanId
                            ? "2px solid #f57c00"
                            : "none",
                        borderRadius: "8px",
                        fontSize: 16,
                        fontWeight: "bold",
                        cursor:
                          isLoading || isCurrentPlan
                            ? "not-allowed"
                            : "pointer",
                        opacity: isLoading && selectedPlan === planId ? 0.6 : 1,
                      }}
                    >
                      {isCurrentPlan
                        ? "✓ Current Plan"
                        : planId === pendingPlanId
                          ? isSameTierChange()
                            ? "Cancel Plan Change"
                            : "Cancel Downgrade"
                          : isLoading && selectedPlan === planId
                            ? "Processing..."
                            : "Switch to This Plan"}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Cancel Subscription Section - Only for Stripe users with active subscription */}
        {canManageInApp && !isCancelled && (
          <div
            className="center_me"
            style={{
              marginTop: 60,
              padding: "0 20px",
              maxWidth: "600px",
              margin: "60px auto 0",
            }}
          >
            <div
              style={{
                backgroundColor: "#1a1a1a",
                padding: "30px",
                borderRadius: "12px",
                border: "1px solid #333",
              }}
            >
              <h3
                className="font_family_base center_me"
                style={{ fontSize: 20, marginBottom: 10 }}
              >
                Cancel Subscription
              </h3>
              <div className="center_me">
                <p
                  className="font_family_base"
                  style={{ color: "#AAA", fontSize: 14, marginBottom: 20 }}
                >
                  You will retain access to your subscription benefits until the
                  end of your current billing period.
                </p>
              </div>
              {!showCancelConfirm ? (
                <div className="center_me">
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    className="font_family_base center_me"
                    style={{
                      backgroundColor: "transparent",
                      color: "#f44336",
                      border: "1px solid #f44336",
                      padding: "10px 24px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: 14,
                      fontWeight: "bold",
                    }}
                  >
                    Cancel Subscription
                  </button>
                </div>
              ) : (
                <div>
                  <div className="center_me">
                    <p
                      className="font_family_base"
                      style={{
                        color: "#f44336",
                        marginBottom: 15,
                        fontSize: 14,
                      }}
                    >
                      Are you sure you want to cancel your subscription?
                    </p>
                  </div>
                  <div
                    className="center_me"
                    style={{ display: "flex", gap: "10px" }}
                  >
                    <button
                      onClick={handleCancelSubscription}
                      disabled={isLoading}
                      className="font_family_base"
                      style={{
                        backgroundColor: "#f44336",
                        color: "white",
                        border: "none",
                        padding: "10px 24px",
                        borderRadius: "8px",
                        cursor: isLoading ? "not-allowed" : "pointer",
                        fontSize: 14,
                        fontWeight: "bold",
                        opacity: isLoading ? 0.6 : 1,
                      }}
                    >
                      {isLoading ? "Cancelling..." : "Yes, Cancel"}
                    </button>
                    <button
                      onClick={() => setShowCancelConfirm(false)}
                      disabled={isLoading}
                      className="font_family_base"
                      style={{
                        backgroundColor: "#333",
                        color: "white",
                        border: "none",
                        padding: "10px 24px",
                        borderRadius: "8px",
                        cursor: isLoading ? "not-allowed" : "pointer",
                        fontSize: 14,
                        fontWeight: "bold",
                      }}
                    >
                      Keep Subscription
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Back Button */}
        <div className="center_me" style={{ marginTop: 30, paddingBottom: 20 }}>
          <button
            onClick={() => navigate("/settings")}
            className="font_family_base"
            style={{
              backgroundColor: "transparent",
              color: "#4CAF50",
              border: "1px solid #4CAF50",
              padding: "10px 24px",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            ← Back to Settings
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}
